import { CSSProperties } from 'react'
import { COLOR_NAME_TO_HEX, ToolTypes } from './helpers'
import { CLOSE_READING } from '@mission.io/question-toolkit'
import { MarkdownTypes } from '@mission.io/question-views'

export const NO_COLOR = '#aaaaaa'

/**
 * getStrikethroughStyle - get the css styles for a strikethrough if the annotation requires the style
 *
 * @param {RangedStyle} rangedStyle - a ranged style possibly containing the strikethrough information
 *
 * @returns {CSSProperties}
 */
export function getStrikethroughStyle(
	rangedStyle: MarkdownTypes.RangedStyle
): CSSProperties | null {
	const strikeThroughColor = rangedStyle.styles.strikeThrough

	if (!strikeThroughColor) {
		return null
	}

	return {
		// this is split into multiple attributes so it can render properly on Safari
		textDecoration: `line-through`,
		textDecorationStyle: 'solid',
		textDecorationColor: strikeThroughColor,
		textDecorationThickness: '2px',
	}
}

/**
 * getHighlightAndUnderlineStyle - get the css styles for highlights and underlining of text if the annotation requires the style
 *
 * @param {RangedStyle} rangedAnnotation - a ranged style possibly containing the underlining and highlights information
 *
 * @returns {CSSProperties}
 */
export function getHighlightAndUnderlineStyle(
	rangedStyle: MarkdownTypes.RangedStyle
): CSSProperties | null {
	const style: CSSProperties = {}
	let hasStyle = false

	const highlightColor = rangedStyle.styles.highlight
	if (highlightColor) {
		style.backgroundColor = highlightColor
		hasStyle = true
	}

	const underlineColor = rangedStyle.styles.underline
	if (underlineColor) {
		// this is split into multiple attributes so it can render properly on Safari
		style.textDecoration = 'underline'
		style.textDecorationStyle = 'solid'
		style.textDecorationColor = underlineColor
		style.textDecorationThickness = '2px'
		hasStyle = true
	}

	if (!hasStyle) {
		return null
	}

	return style
}

/**
 * getSingleAnnotationStyle - get the css styles for a single annotation
 *
 * @param {{style: AnnotationStyle}} annotation - an object containing the annotation style
 *
 * @returns {CSSProperties}
 */
export function getSingleAnnotationStyle({
	style,
}: Readonly<{
	style: { tool?: ToolTypes | null; color?: string | null }
}>): CSSProperties {
	const color =
		(style.color ? COLOR_NAME_TO_HEX[style.color] ?? style.color : null) ??
		NO_COLOR

	if (style.tool === CLOSE_READING.ANNOTATION_TOOL.STRIKE_THROUGH) {
		return {
			// this is split into multiple attributes so it can render properly on Safari
			textDecoration: `line-through`,
			textDecorationStyle: 'solid',
			textDecorationColor: color,
		}
	}
	if (style.tool === CLOSE_READING.ANNOTATION_TOOL.UNDERLINE) {
		return {
			// this is split into multiple attributes so it can render properly on Safari
			textDecoration: 'underline',
			textDecorationStyle: 'solid',
			textDecorationColor: color,
			textDecorationThickness: '2px',
		}
	}
	return {
		backgroundColor: color,
	}
}
